import { NavLink, useMatch, useResolvedPath } from "react-router-dom";
import { DEFAULT_AVATAR, type Patient } from "../api/client";
import { formatMediumDate, formatPhone, titleize } from "../lib/format";

export type PatientHeaderTab = {
  to: string;
  label: string;
  icon: string;
};

/** Map archive patient ids onto their primary conversation thread. */
export function conversationPathForPatient(patientId: string): string {
  const m = String(patientId).match(/archive_patient_(\d+)/i);
  if (m) {
    const n = m[1].padStart(2, "0");
    return `conversations/archive_conversation_${n}`;
  }
  if (patientId === "demo-patient" || patientId === "demo_patient") {
    return "conversations/archive_conversation_01";
  }
  return "conversations/archive_conversation_01";
}

function defaultTabsForPatient(patient: Patient): PatientHeaderTab[] {
  return [
    { to: "activity", label: "Activity", icon: "ne__icon__activity--thin" },
    {
      to: "appointments",
      label: "Appointments",
      icon: "ne__icon__calendar--thin",
    },
    {
      to: conversationPathForPatient(patient.id),
      label: "Messages",
      icon: "ne__icon__messages--thin",
    },
    {
      to: "payments",
      label: "Payment History",
      icon: "ne__icon__credit-card--thin",
    },
  ];
}

export type PatientHeaderProps = {
  patient: Patient;
  tabs?: PatientHeaderTab[];
  /** When false, tabs render as static spans (Storybook without routes). */
  linkTabs?: boolean;
  activeTab?: string;
};

/** Angular puts `.ne__tab` on the `<li>` so sibling margin + active underline work. */
function PatientTabLink({ tab }: { tab: PatientHeaderTab }) {
  const resolved = useResolvedPath(tab.to);
  // Prefix match so conversation sub-routes keep Messages active.
  const match = useMatch({ path: resolved.pathname, end: false });
  const isActive = Boolean(match);
  return (
    <li className={`ne__tab${isActive ? " active" : ""}`}>
      <NavLink to={tab.to} className="ne__tab__link">
        <i className={tab.icon} />
        <span>{tab.label}</span>
      </NavLink>
    </li>
  );
}

/** Patient detail page header with photo, bio table, and tab group. */
export function PatientHeader({
  patient,
  tabs,
  linkTabs = true,
  activeTab,
}: PatientHeaderProps) {
  const resolvedTabs = tabs ?? defaultTabsForPatient(patient);
  return (
    <header className="ne__page-header">
      <div className="ne__page-header__patient">
        <div className="ne__patient__photo">
          <div className="ne__avatar">
            <img
              src={patient.profile_url || DEFAULT_AVATAR}
              alt={patient.name}
            />
          </div>
        </div>
        <div className="ne__patient__details">
          <h2 className="ne__page-header__name">
            {titleize(patient.first_name)} {titleize(patient.last_name)}
          </h2>
          <table className="ne__patient__info">
            <tbody>
              <tr>
                <th>
                  <div className="ne__page-header__eyebrow">Birthday</div>
                </th>
                <td>
                  <div className="ne__page-header__data">
                    {formatMediumDate(
                      patient.bio?.date_of_birth || patient.birthday,
                    )}
                  </div>
                </td>
              </tr>
              {patient.email ? (
                <tr>
                  <th>
                    <div className="ne__page-header__eyebrow">Email</div>
                  </th>
                  <td>
                    <div className="ne__page-header__data">{patient.email}</div>
                  </td>
                </tr>
              ) : null}
              {patient.bio?.phone_number ? (
                <tr>
                  <th>
                    <div className="ne__page-header__eyebrow">Mobile</div>
                  </th>
                  <td>
                    <div className="ne__page-header__data">
                      {formatPhone(patient.bio.phone_number)}
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      <ul className="ne__tab__group">
        {resolvedTabs.map((tab) =>
          linkTabs ? (
            <PatientTabLink key={tab.to} tab={tab} />
          ) : (
            <li
              key={tab.to}
              className={`ne__tab${activeTab === tab.to ? " active" : ""}`}
            >
              <span className="ne__tab__link">
                <i className={tab.icon} />
                <span>{tab.label}</span>
              </span>
            </li>
          ),
        )}
      </ul>
    </header>
  );
}
